import { useState, useMemo } from 'react';
import { useDebounce } from '@hooks/useDebounce';
import { useCxContent } from '@hooks/useCallExperience';
import { parseApiError } from '@utils/errorUtils';
import { OriginBadge } from './cxFormat';

const KIND_LABELS = { builtin: 'Built-in', template: 'Message', question: 'IVR question' };
const SOURCES = [
  ['', 'Any source'], ['company', 'Custom'], ['parent', 'Via group'], ['global', 'Default'],
  ['fallback', 'Code default'], ['builtin', 'Built-in'],
];

/** Every line the journeys speak, resolved for the viewing context, with filters. */
export default function ContentLibrary({ ctx, lang, journeys, onOpen }) {
  const { data: items = [], isLoading, error } = useCxContent(ctx.companyId, lang);
  const [query, setQuery] = useState('');
  const [journey, setJourney] = useState('');
  const [source, setSource] = useState('');
  const [customizedOnly, setCustomizedOnly] = useState(false);
  const [noSpanish, setNoSpanish] = useState(false);
  const [builtinOnly, setBuiltinOnly] = useState(false);
  const debouncedQuery = useDebounce(query, 250);

  const filtered = useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase();
    return items.filter((item) =>
      (!q || item.label.toLowerCase().includes(q) || item.key.toLowerCase().includes(q) || item.text.toLowerCase().includes(q))
      && (!journey || item.usage.some((u) => `${u.journeyId}:${u.productLineId ?? ''}` === journey))
      && (!source || item.origin === source)
      && (!customizedOnly || (ctx.companyId ? item.origin === 'company' : item.customizedCount > 0))
      && (!noSpanish || item.spanishMissing)
      && (!builtinOnly || item.kind === 'builtin'));
  }, [items, debouncedQuery, journey, source, customizedOnly, noSpanish, builtinOnly, ctx.companyId]);

  const journeyTitle = (u) => journeys.find((j) => j.id === u.journeyId && (j.productLineId ?? null) === (u.productLineId ?? null))?.title || u.journeyId;

  return (
    <div className="cx-library">
      <h2 className="cx-jtitle">📚 All content</h2>
      <div className="cx-filters">
        <input type="text" placeholder="Search lines, keys and wording…" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Search" />
        <select value={journey} onChange={(e) => setJourney(e.target.value)} aria-label="Journey">
          <option value="">All journeys</option>
          {journeys.map((j) => <option key={`${j.id}:${j.productLineId ?? ''}`} value={`${j.id}:${j.productLineId ?? ''}`}>{j.title}</option>)}
        </select>
        <select value={source} onChange={(e) => setSource(e.target.value)} aria-label="Source">
          {SOURCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <label><input type="checkbox" checked={customizedOnly} onChange={(e) => setCustomizedOnly(e.target.checked)} />
          {ctx.companyId ? ` Customized for ${ctx.name}` : ' Customized by any company'}</label>
        <label><input type="checkbox" checked={noSpanish} onChange={(e) => setNoSpanish(e.target.checked)} /> Missing Spanish</label>
        <label><input type="checkbox" checked={builtinOnly} onChange={(e) => setBuiltinOnly(e.target.checked)} /> Built-in only</label>
        <span className="cx-muted cx-small">{filtered.length} of {items.length} lines</span>
      </div>
      {error && <div className="cx-error">{parseApiError(error)}</div>}
      {isLoading ? <div className="cx-muted">Loading content…</div> : (
        <table className="cx-table">
          <thead>
            <tr><th>Line</th><th>Type</th><th>Used in</th><th>Wording ({lang === 'es' ? 'Spanish' : 'English'})</th><th>Source</th>{!ctx.companyId && <th>Custom</th>}<th>Spanish</th></tr>
          </thead>
          <tbody>
            {filtered.map((item) => (
              <tr key={`${item.kind}:${item.key}:${item.productLineId ?? ''}`} className="cx-row-click" tabIndex={0}
                onClick={() => onOpen(item)} onKeyDown={(e) => { if (e.key === 'Enter') onOpen(item); }}>
                <td><b>{item.label}</b><div><code className="cx-muted">{item.key}</code></div></td>
                <td>{KIND_LABELS[item.kind]}{item.productLineId ? ` · ${item.productLineId === 2 ? 'CGM' : 'CPAP'}` : ''}</td>
                <td className="cx-small">{[...new Set(item.usage.map(journeyTitle))].join(', ')}</td>
                <td><div className="cx-eff">{item.text?.trim() ? item.text : <i className="cx-muted">silent</i>}</div></td>
                <td><OriginBadge origin={item.origin} /></td>
                {!ctx.companyId && <td>{item.customizedCount || ''}</td>}
                <td>{item.spanishMissing ? <span className="cx-badge cx-badge-warn">missing</span> : '✓'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
